import { LOCAL_USER_DATA, LOGIN_TIMESTAMP, REMEMBER_ME_DATA, USER_DATA, USER_DETAILS_DATA, USER_ROLE, USER_SITE_ID } from '../localisation/index.en';

type AuthData = {
    userId: string | undefined;
    email: string | undefined;
    rememberMeData: { user: string; rememberMe: boolean };
};

/** Safe JSON.parse that returns a fallback value instead of throwing. */
function safeJsonParse<T>(raw: string | null, fallback: T): T {
    if (!raw) return fallback;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

async function readAuthData(): Promise<AuthData> {
    const Data = safeJsonParse<{ user?: string; userId?: string }>(
        window.localStorage.getItem(LOCAL_USER_DATA),
        {}
    );
    const rememberMeData = safeJsonParse<{ user: string; rememberMe: boolean }>(
        window.localStorage.getItem(REMEMBER_ME_DATA),
        { user: '', rememberMe: false }
    );
    return {
        userId: Data.userId,
        email: Data.user,
        rememberMeData,
    };
}

function saveAuthCredentials(user: string, userId: string): void {
    const Data = {
        user: user,
        userId: userId,
    };
    window.localStorage.setItem(LOCAL_USER_DATA, JSON.stringify(Data));
}
function saveRememberMeData(user: string, rememberMe: boolean): void {
    const RememberMeData = {
        user: rememberMe ? user : '',
        rememberMe: rememberMe,
    };
    window.localStorage.setItem(REMEMBER_ME_DATA, JSON.stringify(RememberMeData));
}

/**
 * Clears all authentication-related keys from localStorage.
 * Also removes USER_ROLE, USER_SITE_ID, and LOGIN_TIMESTAMP to prevent
 * stale data from leaking into the next session.
 */
function clearAuthCredentials(): void {
    window.localStorage.removeItem(LOCAL_USER_DATA);
    window.localStorage.removeItem(USER_DATA);
    window.localStorage.removeItem(USER_DETAILS_DATA);
    window.localStorage.removeItem(USER_ROLE);
    window.localStorage.removeItem(USER_SITE_ID);
    window.localStorage.removeItem(LOGIN_TIMESTAMP);
}

function clearRememberMeData(): void {
    window.localStorage.removeItem(REMEMBER_ME_DATA);
}

function getUserData(): any {
    return safeJsonParse<Record<string, unknown>>(
        window.localStorage.getItem(USER_DATA),
        {}
    );
}

function userData(user: any): void {
    window.localStorage.setItem(USER_DATA, JSON.stringify(user));
}
function detailsUserData(user: any): void {
    window.localStorage.setItem(USER_DETAILS_DATA, JSON.stringify(user));
}
function getDetailsUserData(): any {
    return safeJsonParse(window.localStorage.getItem(USER_DETAILS_DATA), {});
}

function setUserRole(role: string): void {
    window.localStorage.setItem(USER_ROLE, role);
}

function setLoginTimestamp(timestamp: number): void {
    window.localStorage.setItem(LOGIN_TIMESTAMP, `${timestamp}`);
}

function getLoginTimestamp(): number {
    const timestamp = window.localStorage.getItem(LOGIN_TIMESTAMP) || '';
    const parsed = parseInt(timestamp, 10);
    return isNaN(parsed) ? 0 : parsed;
}

/**
 * Returns the stored user role as a string, or an empty string if none is stored.
 * The role is stored as a plain string (not JSON), so we read it directly.
 */
function getUserRole(): string {
    return window.localStorage.getItem(USER_ROLE) ?? '';
}

function setSiteId(siteId: string): void {
    window.localStorage.setItem(USER_SITE_ID, siteId);
}

function getSiteId(): string | null {
    return window.localStorage.getItem(USER_SITE_ID);
}

export const LocalStorage = {
    readAuthData,
    saveAuthCredentials,
    saveRememberMeData,
    clearAuthCredentials,
    getUserData,
    getUserRole,
    userData, getSiteId, setSiteId,
    setUserRole,
    clearRememberMeData,
    getLoginTimestamp,
    setLoginTimestamp,
    detailsUserData,
    getDetailsUserData
};
